import {
  BatteryLow, Power, BatteryWarning, Thermometer, Gauge, ShieldCheck, Zap, Clock, MapPin, Star,
} from 'lucide-react';
import Seo from '../../components/Seo';
import ServicePageLayout from '../../components/services/ServicePageLayout';
import { ServiceSection, ServiceFAQ, RelatedServices } from '../../components/services/ServiceUI';
import {
  ServiceHeroPro, ProofStrip, DiagnosisList, PriceCardPro, AvisStrip, LocalCTA,
} from '../../components/services/elevated';
import { REVIEWS, RATING, REVIEW_COUNT } from '../../data/reviews';

const SLUG = '/changement-batterie-iphone-montpellier';

const faq = [
  { q: "Combien de temps dure le remplacement ?", a: "Environ 30 minutes, pendant que vous attendez, sans rendez-vous." },
  { q: "Vais-je perdre mes données en changeant la batterie ?", a: "Non, le remplacement de batterie n'efface aucune donnée." },
  { q: "Le message « Batterie » ou l'alerte de santé disparaîtra-t-il ?", a: "Avec une batterie de qualité et un recalibrage, l'autonomie est restaurée. Certains messages liés à l'authentification de la pièce peuvent subsister selon le modèle ; nous vous l'indiquons avant l'intervention." },
  { q: "Ma batterie gonfle, est-ce dangereux ?", a: "Une batterie qui gonfle doit être remplacée sans tarder. Apportez l'appareil rapidement et évitez de le charger." },
];

const signes = [
  { icon: BatteryLow, title: "Autonomie en chute", desc: "L'iPhone se décharge en quelques heures." },
  { icon: Power, title: "Extinctions soudaines", desc: "S'éteint parfois alors qu'il reste de la charge." },
  { icon: BatteryWarning, title: "Santé sous 80 %", desc: "Visible dans Réglages › Batterie." },
  { icon: Thermometer, title: "Chauffe ou gonfle", desc: "À traiter rapidement pour votre sécurité." },
  { icon: Gauge, title: "Ralentissements", desc: "Liés à la limitation de performance d'iOS." },
];

const proofItems = [
  { icon: Zap, label: 'Réparé en 30 min' },
  { icon: ShieldCheck, label: 'Garantie 6 mois' },
  { icon: Clock, label: 'Sans rendez-vous' },
  { icon: Star, label: '4,7/5 · 579 avis' },
  { icon: MapPin, label: 'Centre-ville Montpellier' },
];

const ChangementBatterieIphone = () => (
  <ServicePageLayout trail={[{ name: 'Accueil', to: '/' }, { name: 'Réparation iPhone', to: '/reparation-iphone-montpellier' }, { name: 'Changement de batterie' }]}>
    <Seo
      title="Changement Batterie iPhone Montpellier — dès 25€ | AlloRép."
      description="Remplacement de batterie iPhone à Montpellier en 30 min : autonomie restaurée, pièce de qualité, devis gratuit, garantie 6 mois."
      canonicalPath={SLUG}
      service={{
        name: "Changement de batterie iPhone à Montpellier",
        serviceType: "Remplacement de batterie iPhone",
        description: "Remplacement de batterie iPhone à Montpellier en 30 minutes, autonomie restaurée, garantie 6 mois.",
        offers: { '@type': 'Offer', priceCurrency: 'EUR', price: '25', description: "À partir de 25 € selon le modèle d'iPhone" },
      }}
      faq={faq}
      breadcrumb={[
        { name: 'Accueil', path: '/' },
        { name: 'Réparation iPhone', path: '/reparation-iphone-montpellier' },
        { name: 'Changement de batterie iPhone à Montpellier', path: SLUG },
      ]}
    />

    <ServiceHeroPro
      eyebrow="Batterie iPhone — Montpellier"
      h1Before="Changement de "
      h1Highlight="batterie iPhone"
      h1After=" à Montpellier"
      intro="Votre iPhone se décharge en quelques heures, s'éteint avant 20 %, ou affiche une santé de batterie dégradée ? AlloRéparations remplace votre batterie iPhone à Montpellier en 30 minutes, sans rendez-vous, et vous rend un téléphone qui tient de nouveau la journée. Devis gratuit, garantie 6 mois."
      image={{ src: '/iphones/hub-iphone-15-pro.jpg', alt: "Changement de batterie iPhone à Montpellier — iPhone 15 Pro" }}
      rating={{ value: RATING, count: REVIEW_COUNT }}
    />

    <ProofStrip items={proofItems} />

    <DiagnosisList title="Les signes qu'il faut changer la batterie" items={signes} />

    <ServiceSection title="Une batterie neuve, pas une rustine">
      <p>
        Nous remplaçons la batterie usée par une batterie neuve de qualité, puis recalibrons l'appareil. Vous
        retrouvez l'autonomie d'origine et, sur les modèles concernés, la fin du bridage de performance imposé par
        iOS aux batteries trop dégradées.
      </p>
    </ServiceSection>

    <PriceCardPro
      eyebrow="Tarifs par modèle"
      title="Modèles et tarifs"
      columns={["Modèle d'iPhone", 'Délai', 'Prix TTC']}
      rows={[
        ['iPhone 11 / 12 / 13', '30 min', '45–65 €'],
        ['iPhone 14 / 15', '30 min', "jusqu'à 99 €"],
        ['iPhone X / XR / XS', '30 min', '45 €'],
        ['iPhone 8 et antérieurs', '30 min', '25 €'],
      ]}
    />

    <AvisStrip rating={RATING} count={REVIEW_COUNT} reviews={REVIEWS.slice(2, 5)} />

    <ServiceFAQ faq={faq} />

    <LocalCTA heading="Batterie fatiguée ? Retrouvez la journée complète." sub="Batterie neuve posée en 30 minutes, sans rendez-vous, garantie 6 mois." />

    <RelatedServices
      links={[
        { label: 'Réparation iPhone (toutes pannes)', to: '/reparation-iphone-montpellier', desc: 'La page pilier iPhone.' },
        { label: "Réparation d'écran iPhone", to: '/reparation-ecran-iphone-montpellier', desc: 'Vitre, dalle, tactile — dès 45 €.' },
      ]}
    />
  </ServicePageLayout>
);

export default ChangementBatterieIphone;
